import React, { useState, useMemo, memo, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import DefectImageUploader from '../../feature-bundle/key-findings/defect-grid/DefectImageUploader';
import customAxios from '../../../../api/customAxios';
import { authHeader } from '../../../../authentication/authTokenService';
import { saveImages } from '../../../../components/imageUploader/imageProcessing';
import { saveDefectImage } from '../../api/defectImageApi';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  AutoAwesome as Generate,
  PhotoCamera as Camera,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Close
} from '@mui/icons-material';
import { getIntlTranslation } from '../../../../utils/helper';

const DefectImageUploaderWithGenerate = ({ 
  row, 
  values, 
  setFieldValue, 
  showGenerateButton = true,
  onClose,
  needGridRefresh,
  onSaveImages, // New prop to handle image saving from parent
  ...otherProps 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [componentId] = useState(() => `defect-uploader-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [detectedDefects, setDetectedDefects] = useState([]);
  const [showDefectSelection, setShowDefectSelection] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [imageCache, setImageCache] = useState({ 
    hasImages: false, 
    lastDefectId: null,
    lastImageCount: 0,
    lastReduxLength: 0
  });
  
  // 🆕 CLIENT-SIDE BUFFERING: Store images in memory before defect creation
  const [bufferedImages, setBufferedImages] = useState([]);
  const [uploaderFiles, setUploaderFiles] = useState([]); // 🆕 Track DefectImageUploader's files
  const [externalCaptionUpdate, setExternalCaptionUpdate] = useState(null); // 🆕 Trigger caption updates

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const scope = useSelector((state) => state.container.scope);
  const imageState = useSelector((state) => state.imageProcess);
  const elementState = useSelector((state) => state.elementProcess);

  // Robust function to detect images from multiple sources - wrapped in useCallback to prevent stale closures
  const detectImages = React.useCallback(() => {
    try {
      let imageCount = 0;
      let sources = [];

      // Source 0: 🆕 BUFFERED IMAGES (new defect mode - highest priority!)
      // These are images uploaded but waiting for defectIdent
      if (bufferedImages && bufferedImages.length > 0) {
        imageCount += bufferedImages.length;
        sources.push(`Buffered: ${bufferedImages.length} (waiting for defectIdent)`);
      }

      // Source 0.5: 🆕 UPLOADER FILES (DefectImageUploader's internal files state)
      // These are images in the child DefectImageUploader component
      if (uploaderFiles && uploaderFiles.length > 0) {
        imageCount += uploaderFiles.length;
        sources.push(`UploaderFiles: ${uploaderFiles.length} (in DefectImageUploader)`);
      }

      // Source 1: Redux state (when available and not reset)
      // CRITICAL: Check for empty array/object explicitly - this means images were deleted
      if (imageState?.data && Array.isArray(imageState.data)) {
        if (imageState.data.length > 0) {
          imageCount += imageState.data.length;
          sources.push(`Redux: ${imageState.data.length}`);
        } else {
          // Empty array means no images in Redux
          sources.push(`Redux: 0 (empty array)`);
        }
      } else if (imageState?.data && typeof imageState.data === 'object' && !Array.isArray(imageState.data)) {
        // Object format - check if it has meaningful keys (not just empty reset state)
        const keys = Object.keys(imageState.data);
        if (keys.length === 0) {
          sources.push(`Redux: {} (empty object - reset)`);
        } else {
          // Object with data - might be from existing defect
          imageCount += 1;
          sources.push(`Redux: object with ${keys.length} keys`);
        }
      } else if (imageState?.data === null || imageState?.data === undefined) {
        // null/undefined means Redux state was cleared
        sources.push(`Redux: null/undefined (cleared)`);
      }

      // Source 2: Row data (reliable for existing defects)
      // BUT: Don't trust row.imageCount after deletion - Redux is more current
      if (row?.defectIdent && row?.imageCount > 0) {
        // Only count row images if we don't have Redux data at all (not even empty array)
        if (imageState?.data === null || imageState?.data === undefined) {
          imageCount += row.imageCount;
          sources.push(`Row: ${row.imageCount} (Redux null)`);
        } else {
          // Redux exists (even if empty), so row count is stale
          sources.push(`Row: ${row.imageCount} (ignored - Redux present)`);
        }
      }

      // Source 3: DOM scan for uploaded images (blob URLs and saved images)
      // This is the most reliable for detecting actual visible images
      const domImages = document.querySelectorAll('img');
      const relevantImages = Array.from(domImages).filter(img => {
        const src = img.src || '';
        const width = img.width || 0;
        const height = img.height || 0;
        
        // Skip tiny images, icons, and system images
        if (width < 100 || height < 100) return false;
        if (src.includes('logo') || src.includes('icon') || src.includes('avatar')) return false;
        
        // Include defect-related images
        return src.includes('blob:') || 
               src.includes('base64') || 
               src.includes('amazonaws') || 
               src.includes('cloudfront') ||
               src.includes('defect') ||
               (width > 150 && height > 100);
      });

      if (relevantImages.length > 0) {
        // Only count DOM images if we don't have other sources
        if (imageCount === 0) {
          imageCount += relevantImages.length;
        }
        sources.push(`DOM: ${relevantImages.length}`);
      }

      const hasImages = imageCount > 0;
      
      return { hasImages, imageCount, sources };
    } catch (error) {
      return { hasImages: false, imageCount: 0, sources: [] };
    }
  }, [bufferedImages, uploaderFiles, imageState?.data, row?.defectIdent, row?.imageCount]);

  // useSelector hook for scope data (keeping existing functionality)

  // Function to check if there are unsaved images
  const checkForUnsavedImages = () => {
    try {
      // Check if there are unsaved images in the DOM
      const saveButtons = document.querySelectorAll('button');
      const hasSaveAllButton = Array.from(saveButtons).some(button => 
        button.textContent?.includes('SAVE IMAGE') || 
        button.textContent?.includes('Save Image')
      );

      if (hasSaveAllButton) {
        return true; // There are unsaved images
      }

      // Alternative check - look for image elements with unsaved status
      const imageContainers = document.querySelectorAll('[class*="image"]');
      const hasUnsavedImages = Array.from(imageContainers).some(container => {
        const text = container.textContent?.toLowerCase() || '';
        return text.includes('unsaved') || text.includes('pending');
      });

      return hasUnsavedImages;
    } catch (error) {
      return false;
    }
  };

  // 🆕 Function to add image to buffer (when no defectIdent)
  const addToBuffer = React.useCallback((file, imageFile) => {
    const bufferedImage = {
      id: file.id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file: imageFile,
      fileName: file.fileName,
      preview: file.src || URL.createObjectURL(imageFile),
      caption: file.caption || '',
      order: bufferedImages.length + 1,
      primary: bufferedImages.length === 0,
      status: 'buffered'
    };
    
    setBufferedImages(prev => [...prev, bufferedImage]);
    enqueueSnackbar('Image buffered - will be uploaded after defect creation', { 
      variant: 'info',
      autoHideDuration: 3000 
    });
  }, [bufferedImages, enqueueSnackbar]);

  // 🆕 Function to upload buffered images with defectIdent
  const uploadBufferedImages = React.useCallback(async (defectIdent, projectIdent) => {
    // Collect images from THREE sources:
    // 1. bufferedImages state (explicit buffer)
    // 2. Redux imageProcess.data (might have images from other flows)
    // 3. uploaderFiles (DefectImageUploader's internal files state)
    const reduxImages = imageState?.data && Array.isArray(imageState.data) ? imageState.data : [];
    const defectUploaderImages = uploaderFiles.filter(f => f.status === 'new' || f.status === 'buffered');
    const allImagesToUpload = [...bufferedImages, ...reduxImages, ...defectUploaderImages];
    
    if (allImagesToUpload.length === 0) {
      return { success: true, count: 0 };
    }

    try {
      const uploadPromises = allImagesToUpload.map((img, index) => {
        const formData = new FormData();
        formData.append("type", "imageMetaDto");
        formData.append("imageIdent", img.id || img.imageIdent || img.key || `temp-${Date.now()}-${index}`);
        formData.append("parentIdent", defectIdent);
        formData.append("primary", index === 0);
        formData.append("deleted", false);
        formData.append("caption", img.caption || " ");
        formData.append("fileName", img.fileName || img.name || "image.jpg");
        formData.append("order", index + 1);
        formData.append("assetIdent", "");
        formData.append("edited", img.isEdited || false);
        formData.append("source", "upload");
        
        // Get the actual File object for upload
        const imageFile = img instanceof File ? img : 
                          img._originalFile || img.file || img.imageFile;
        
        if (!(imageFile instanceof File)) {
          return Promise.reject(new Error('Invalid file object'));
        }
        
        formData.append("multipartFile", imageFile);

        return dispatch(saveDefectImage({
          formData,
          projectIdent: projectIdent,
          id: img.id || img.imageIdent
        }));
      });

      await Promise.all(uploadPromises);
      
      // Clear buffer after successful upload
      setBufferedImages([]);
      
      return { success: true, count: allImagesToUpload.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [bufferedImages, uploaderFiles, imageState, dispatch]);

  // Expose image saving functionality and buffered images to parent
  React.useEffect(() => {
    if (onSaveImages) {
      // 🆕 Check BOTH bufferedImages AND uploaderFiles for new defect mode
      const totalBufferedCount = bufferedImages.length + uploaderFiles.length;
      const hasAnyBuffered = totalBufferedCount > 0;
      
      onSaveImages({
        checkForUnsavedImages,
        hasUnsavedImages: checkForUnsavedImages(),
        // 🆕 Expose buffered images to parent (CreateDefect)
        bufferedImages: bufferedImages,
        uploaderFiles: uploaderFiles, // 🆕 Also expose DefectImageUploader files
        hasBufferedImages: hasAnyBuffered, // TRUE if either has images
        uploadBufferedImages: uploadBufferedImages,
        addToBuffer: addToBuffer
      });
    }
  }, [onSaveImages, bufferedImages, uploaderFiles, uploadBufferedImages, addToBuffer]);

  // 🆕 Handler to receive files from DefectImageUploader child component
  const handleUploaderFilesChange = React.useCallback((files) => {
    setUploaderFiles(files || []);
  }, []);

  // Update button state whenever relevant data changes
  React.useEffect(() => {
    const currentDefectId = row?.defectIdent || 'new';
    const currentReduxLength = imageState?.data?.length || 0;
    const currentRowImageCount = row?.imageCount || 0;
    
    // CRITICAL: If Redux data is explicitly an empty array OR empty object, disable immediately
    const isReduxEmpty = (Array.isArray(imageState?.data) && imageState.data.length === 0) ||
                         (imageState?.data && typeof imageState.data === 'object' && !Array.isArray(imageState.data) && Object.keys(imageState.data).length === 0);
    
    if (isReduxEmpty) {
      setIsButtonEnabled(false);
      setImageCache({
        hasImages: false,
        lastDefectId: currentDefectId,
        lastImageCount: currentRowImageCount,
        lastReduxLength: 0
      });
      return; // Exit early
    }
    
    // Detect images using our robust detection function
    const { hasImages, imageCount } = detectImages();
    
    // Always update button state based on current detection
    // This ensures button disables immediately when images are deleted
    setIsButtonEnabled(hasImages);
    
    // Update cache for tracking purposes
    setImageCache({
      hasImages,
      lastDefectId: currentDefectId,
      lastImageCount: currentRowImageCount,
      lastReduxLength: currentReduxLength
    });
    
  }, [
    row?.defectIdent, 
    row?.imageCount, 
    imageState?.data, 
    imageState?.type,
    detectImages
  ]);

  // Reset cache when defect changes
  React.useEffect(() => {
    const newDefectId = row?.defectIdent || 'new';
    // Check if defect actually changed by comparing with current cache
    if (imageCache.lastDefectId && imageCache.lastDefectId !== newDefectId) {
      setImageCache({ 
        hasImages: false, 
        lastDefectId: newDefectId,
        lastImageCount: 0,
        lastReduxLength: 0
      });
      setIsButtonEnabled(false);
    }
  }, [row?.defectIdent]);

  // Additional effect to handle immediate updates after image saves
  React.useEffect(() => {
    if (imageState?.type === 'SUCCESS') {
      // Small delay to let DOM update
      setTimeout(() => {
        const { hasImages } = detectImages();
        setIsButtonEnabled(hasImages);
        if (hasImages) {
          setImageCache(prev => ({ ...prev, hasImages: true }));
        } else {
          setImageCache(prev => ({ ...prev, hasImages: false }));
        }
      }, 100);
    }
  }, [imageState?.type, detectImages]);

  // Handle image deletion - critical for disabling button when images are deleted
  React.useEffect(() => {
    if (imageState?.type === 'DELETED' || imageState?.type === 'RESET') {
      // Immediate disable - don't wait for DOM
      setIsButtonEnabled(false);
      setImageCache(prev => ({ ...prev, hasImages: false }));
      
      // Then verify after delay
      setTimeout(() => {
        const { hasImages } = detectImages();
        setIsButtonEnabled(hasImages);
        setImageCache(prev => ({ ...prev, hasImages }));
      }, 150);
    }
  }, [imageState?.type, detectImages]);

  // Periodic check for images (as a safety net for async image loading)
  React.useEffect(() => {
    let intervalId;
    let attemptCount = 0;
    const maxAttempts = 5;
    
    // Only run periodic checks if we think there should be images but need to verify
    if (row?.defectIdent && row?.imageCount > 0) {
      intervalId = setInterval(() => {
        attemptCount++;
        const { hasImages } = detectImages();
        setIsButtonEnabled(hasImages);
        
        if (hasImages) {
          setImageCache(prev => ({ ...prev, hasImages: true }));
          clearInterval(intervalId);
        } else if (attemptCount >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, 1000); // Check every second for up to 5 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [row?.defectIdent, row?.imageCount, detectImages]);

  // Initial image detection when component mounts or defect changes
  // This handles the case when reopening a defect that already has images
  React.useEffect(() => {
    // Small delay to allow images to load in the DOM
    const initialCheckTimer = setTimeout(() => {
      const { hasImages, imageCount } = detectImages();
      
      if (hasImages) {
        setIsButtonEnabled(true);
        setImageCache(prev => ({ ...prev, hasImages: true }));
      }
    }, 500); // Wait 500ms for images to load

    return () => clearTimeout(initialCheckTimer);
  }, [row?.defectIdent, detectImages]); // Re-run when defect changes

  // Create stable row object to prevent infinite API calls
  const stableRow = useMemo(() => {
    if (!row) {
      return {
        defectIdent: null,
        trade: { value: '' },
        type: 'defect_row',
        deleted: false,
        imageCount: 0,
        uniqueId: `${componentId}-new-defect`
      };
    }
    
    return {
      defectIdent: row.defectIdent || null,
      trade: row.trade || { value: '' },
      type: row.type || 'defect_row',
      deleted: row.deleted || false,
      imageCount: row.imageCount || 0,
      uniqueId: `${componentId}-${row.defectIdent || 'new-defect'}`
    };
  }, [
    componentId,
    row?.defectIdent,
    row?.trade?.value,
    row?.type,
    row?.deleted,
    row?.imageCount
  ]);

  // Handle close button - prevent errors
  const handleClose = (needRefresh) => {
    try {
      if (onClose && typeof onClose === 'function') {
        onClose(needRefresh);
      } else if (needGridRefresh && typeof needGridRefresh === 'function') {
        needGridRefresh(needRefresh);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/...;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Get image data from DefectImageUploader component
  const getUploadedImageData = async (imageProcessState) => {
    try {
      // Method 1: Check Redux state for images (DefectImageUploader uses imageProcess state)
      
      // Method 2: Look for images with blob URLs (createObjectURL)
      const blobImages = document.querySelectorAll('img[src^="blob:"]');
      
      if (blobImages.length > 0) {
        const blobUrl = blobImages[0].src;
        
        // Convert blob URL to base64
        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
              resolve(result);
            };
            reader.readAsDataURL(blob);
          });
          return base64;
        } catch (error) {
          // Handle error silently
        }
      }

      // Method 3: Look for file input elements and check their files
      const fileInputs = document.querySelectorAll('input[type="file"][accept*="image"]');
      
      for (let input of fileInputs) {
        if (input.files && input.files.length > 0) {
          const file = input.files[0];
          return await fileToBase64(file);
        }
      }

      // Method 5: Look for existing images with base64 src
      const base64Images = document.querySelectorAll('img[src^="data:image"]');
      
      if (base64Images.length > 0) {
        const fullSrc = base64Images[0].src;
        return fullSrc.split(',')[1]; // Remove data:image/...;base64, prefix
      }

      // Method 6: Check for images in dropzone or upload areas
      const containerSelectors = [
        '[data-testid="dropzone"]', 
        '.dropzone', 
        '[class*="dropzone"]',
        '[class*="image-upload"]',
        '[class*="file-upload"]'
      ];
      
      for (let selector of containerSelectors) {
        const containers = document.querySelectorAll(selector);
        for (let container of containers) {
          const images = container.querySelectorAll('img[src^="blob:"], img[src^="data:image"]');
          if (images.length > 0) {
            const src = images[0].src;
            if (src.startsWith('blob:')) {
              // Convert blob to base64
              try {
                const response = await fetch(src);
                const blob = await response.blob();
                return await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const result = reader.result.split(',')[1];
                    resolve(result);
                  };
                  reader.readAsDataURL(blob);
                });
              } catch (error) {
              }
            } else if (src.startsWith('data:image')) {
              return src.split(',')[1];
            }
          }
        }
      }

      return null;
      
    } catch (error) {
      return null;
    }
  };

  const handleGenerateDefect = async () => {
    if (!values || !setFieldValue) {
      enqueueSnackbar('Cannot generate defect: missing form data', { variant: 'error' });
      return;
    }

    // Double-check button state with fresh image detection
    const { hasImages } = detectImages();
    if (!hasImages && !isButtonEnabled) {
      enqueueSnackbar('Please upload an image before generating AI analysis', { variant: 'warning' });
      return;
    }

    // If we detect images but button was disabled, enable it
    if (hasImages && !isButtonEnabled) {
      setIsButtonEnabled(true);
      setImageCache(prev => ({ ...prev, hasImages: true }));
    }

    setIsGenerating(true);
    setGenerateError(null);

    try {
      // Get the base64 image data
      const imageBase64 = await getUploadedImageData(imageState);

      if (!imageBase64) {
        // Final fallback: if we still can't get image data, inform user
        enqueueSnackbar('Could not access image data. Please ensure images are properly uploaded and saved.', { variant: 'warning' });
        setIsGenerating(false);
        return;
      }

      // Get projectIdent and subCategoryUUID from the scope and values
      const projectIdent = scope.projectIdent;
      const subCategoryUUID = values?.subCategory?.ident;

      // Validate required parameters
      if (!projectIdent || !subCategoryUUID) {
        enqueueSnackbar('Cannot analyze image: missing project or category information', { variant: 'error' });
        setIsGenerating(false);
        return;
      }

      // Prepare the correct API payload
      const analysisPayload = {
        imageBase64: imageBase64,
        language: scope.projectLanguageCode || 'en'
      };

      // Call the actual API with correct payload and URL (new endpoint with path parameters)
      // Note: customAxios already has baseURL set to /api, so we don't need leading slash
      const response = await customAxios.post(
        `openai/analyze-image/${projectIdent}/${subCategoryUUID}`, 
        analysisPayload, 
        {
          headers: {
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        const analysis = response.data;

        // Parse different response formats for defects
        let defects = null;
        if (analysis.data?.defects && Array.isArray(analysis.data.defects)) {
          defects = analysis.data.defects;
        } else if (analysis.defects && Array.isArray(analysis.defects)) {
          defects = analysis.defects;
        } else if (analysis.suggestions && Array.isArray(analysis.suggestions)) {
          defects = analysis.suggestions;
        } else if (Array.isArray(analysis)) {
          defects = analysis;
        }

        // Handle defects response
        if (defects && defects.length > 0) {
          
          if (defects.length === 1) {
            // Single defect - auto-fill form
            const singleDefect = defects[0];
            
            // Auto-fill form fields based on API response structure
            if (singleDefect.description || singleDefect.defect || singleDefect.title) {
              const newDescription = singleDefect.description || singleDefect.defect || singleDefect.title;
              const currentDescription = values.defectDescription || '';
              
              const finalDescription = currentDescription.trim() 
                ? currentDescription.trim() + '\n' + newDescription 
                : newDescription;
              
              setFieldValue('defectDescription', finalDescription);
            }
            
            // Set AI-generated caption to image files if available
            if (analysis.caption) {
              // Update captions for buffered images
              setBufferedImages(currentBuffered => 
                currentBuffered.map(img => ({
                  ...img,
                  caption: analysis.caption
                }))
              );
              
              // Trigger external caption update for DefectImageUploader
              setExternalCaptionUpdate({
                caption: analysis.caption,
                timestamp: Date.now(), // Force re-render
                onComplete: () => setExternalCaptionUpdate(null) // Reset after update
              });
            }
            
            if (singleDefect.recommended_action || singleDefect.action || singleDefect.corrective_measure || singleDefect.correctiveMeasure) {
              const newMeasure = singleDefect.recommended_action || 
                singleDefect.action || 
                singleDefect.corrective_measure || 
                singleDefect.correctiveMeasure;
              
              const currentMeasure = values.measureDescription || '';
              
              const finalMeasure = currentMeasure.trim() 
                ? currentMeasure.trim() + '\n' + newMeasure 
                : newMeasure;
              
              setFieldValue('measureDescription', finalMeasure);
            }
            
            // Set element type if available
            if (singleDefect.elementType || singleDefect.element_type || singleDefect.element) {
              const elementTypeValue = singleDefect.elementType || singleDefect.element_type || singleDefect.element;
              
              // 🔧 FIX: Handle AI-generated element types that don't match dropdown options
              // The form's Autocomplete will try to match this value, but if it fails,
              // we should select the first "Others" option and put the AI value in customElement
              
              // First, try to set the elementType - the form will attempt to match it
              setFieldValue('elementType', elementTypeValue);
              
              // Set a flag to indicate this came from AI, so the form can handle it specially
              setFieldValue('aiGeneratedElementType', elementTypeValue);
            }
            
            if (singleDefect.priority) {
              setFieldValue('priority', singleDefect.priority);
            }
            
            if (singleDefect.risk_type || singleDefect.riskType || singleDefect.risk_level) {
              setFieldValue('riskType', 
                singleDefect.risk_type || 
                singleDefect.riskType || 
                singleDefect.risk_level
              );
            }
            
            if (singleDefect.recommendations || singleDefect.advice) {
              setFieldValue('recommendations', singleDefect.recommendations || singleDefect.advice);
            }
            
            enqueueSnackbar('Defect analysis completed and form filled!', { variant: 'success' });
            
            // Scroll to the element type field at the top to show generated content
            setTimeout(() => {
              // Find the dialog content container and scroll it to top with smooth animation
              const dialogContent = document.querySelector('.MuiDialogContent-root');
              const formElement = document.querySelector('form');
              
              if (dialogContent) {
                // Smooth scroll the dialog content to top
                dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
              } else if (formElement) {
                // Smooth scroll the form itself
                formElement.scrollTo({ top: 0, behavior: 'smooth' });
              }
              
              // Also scroll window to top with smooth animation
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 300);
            
          } else {
            // Multiple defects - show selection dialog
            setDetectedDefects(defects);
            setShowDefectSelection(true);
            enqueueSnackbar(`Found ${defects.length} possible defects. Please choose one.`, { variant: 'info' });
          }
        } else {
          // Legacy single defect format - handle direct response fields
          let fieldsUpdated = false;
          
          if (analysis.description || analysis.defectDescription) {
            const newDescription = analysis.description || analysis.defectDescription;
            const currentDescription = values.defectDescription || '';
            
            const finalDescription = currentDescription.trim() 
              ? currentDescription.trim() + '\n' + newDescription 
              : newDescription;
            
            setFieldValue('defectDescription', finalDescription);
            fieldsUpdated = true;
          }
          
          // Set AI-generated caption to image files if available (legacy format)
          if (analysis.caption) {
            // Update captions for buffered images
            setBufferedImages(currentBuffered => 
              currentBuffered.map(img => ({
                ...img,
                caption: analysis.caption
              }))
            );
            
            // Trigger external caption update for DefectImageUploader
            setExternalCaptionUpdate({
              caption: analysis.caption,
              timestamp: Date.now(), // Force re-render
              onComplete: () => setExternalCaptionUpdate(null) // Reset after update
            });
            fieldsUpdated = true;
          }
          
          if (analysis.measureDescription || analysis.measure_description) {
            const newMeasure = analysis.measureDescription || analysis.measure_description;
            const currentMeasure = values.measureDescription || '';
            
            const finalMeasure = currentMeasure.trim() 
              ? currentMeasure.trim() + '\n' + newMeasure 
              : newMeasure;
            
            setFieldValue('measureDescription', finalMeasure);
            fieldsUpdated = true;
          }
          
          // Set element type if available in legacy format
          if (analysis.elementType || analysis.element_type || analysis.element) {
            setFieldValue('elementType', analysis.elementType || analysis.element_type || analysis.element);
            fieldsUpdated = true;
          }
          
          if (analysis.priority) {
            setFieldValue('priority', analysis.priority);
            fieldsUpdated = true;
          }
          
          if (analysis.riskType || analysis.risk_type || analysis.risk_level) {
            setFieldValue('riskType', 
              analysis.riskType || 
              analysis.risk_type || 
              analysis.risk_level
            );
            fieldsUpdated = true;
          }
          
          if (analysis.recommendations || analysis.advice) {
            setFieldValue('recommendations', analysis.recommendations || analysis.advice);
            fieldsUpdated = true;
          }
          
          if (fieldsUpdated) {
            enqueueSnackbar('Defect analysis completed and form filled!', { variant: 'success' });
            
            // Scroll to the element type field at the top to show generated content
            setTimeout(() => {
              // Find the dialog content container and scroll it to top with smooth animation
              const dialogContent = document.querySelector('.MuiDialogContent-root');
              const formElement = document.querySelector('form');
              
              if (dialogContent) {
                // Smooth scroll the dialog content to top
                dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
              } else if (formElement) {
                // Smooth scroll the form itself
                formElement.scrollTo({ top: 0, behavior: 'smooth' });
              }
              
              // Also scroll window to top with smooth animation
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 300);
          } else {
            enqueueSnackbar('AI analysis completed but no suitable data found for auto-fill', { variant: 'warning' });
          }
        }

      } else {
        throw new Error('API returned empty response');
      }

    } catch (error) {
      
      // Handle different types of errors appropriately
      if (error.code === 'ECONNREFUSED' || error.message.includes('localhost')) {
        // Development environment - API server not running
        
        const mockSuggestions = {
          defectDescription: "AI-analyzed defect: Visual inspection reveals potential structural/maintenance concern requiring professional assessment. Image analysis suggests material degradation or damage that needs immediate attention.",
          measureDescription: "Conduct detailed professional inspection to assess extent of damage. Clean and prepare affected area. Apply appropriate repair method based on material type and damage severity. Schedule follow-up inspection.",
          priority: "medium",
          riskType: "medium",
          recommendations: "Engage qualified professional for detailed assessment. Document current condition with additional photos if needed. Monitor for progression. Consider preventive measures for similar areas."
        };

        // Update form fields with mock suggestions
        Object.keys(mockSuggestions).forEach(key => {
          if (mockSuggestions[key] && setFieldValue) {
            setFieldValue(key, mockSuggestions[key]);
          }
        });

        enqueueSnackbar('AI analysis completed with mock data (development mode)', { 
          variant: 'info',
          autoHideDuration: 4000
        });
      } else if (error.response?.status === 404 || error.response?.status === 500) {
        // API endpoint not available
        enqueueSnackbar('AI analysis service temporarily unavailable', { 
          variant: 'warning',
          autoHideDuration: 4000
        });
      } else {
        // Other errors (CORS, network, etc.)
        enqueueSnackbar(`AI analysis failed: ${error.message || 'Network error'}`, { 
          variant: 'error',
          autoHideDuration: 4000
        });
      }

    } finally {
      setIsGenerating(false);
    }
  };

  // Select a defect from multiple options
  const handleDefectSelect = (selectedDefect) => {
    // Auto-fill form fields with selected defect
    if (selectedDefect.description || selectedDefect.defect || selectedDefect.title) {
      const newDescription = selectedDefect.description || selectedDefect.defect || selectedDefect.title;
      const currentDescription = values.defectDescription || '';
      
      const finalDescription = currentDescription.trim() 
        ? currentDescription.trim() + '\n' + newDescription 
        : newDescription;
      
      setFieldValue('defectDescription', finalDescription);
    }
    
    // Set AI-generated caption to image files if available in selected defect
    if (selectedDefect.caption) {
      // Update captions for buffered images
      setBufferedImages(currentBuffered => 
        currentBuffered.map(img => ({
          ...img,
          caption: selectedDefect.caption
        }))
      );
      
      // Trigger external caption update for DefectImageUploader
      setExternalCaptionUpdate({
        caption: selectedDefect.caption,
        timestamp: Date.now(), // Force re-render
        onComplete: () => setExternalCaptionUpdate(null) // Reset after update
      });
    }
    
    if (selectedDefect.measureDescription || selectedDefect.measure_description || selectedDefect.recommended_action) {
      const newMeasure = selectedDefect.measureDescription || selectedDefect.measure_description || selectedDefect.recommended_action;
      const currentMeasure = values.measureDescription || '';
      
      const finalMeasure = currentMeasure.trim() 
        ? currentMeasure.trim() + '\n' + newMeasure 
        : newMeasure;
      
      setFieldValue('measureDescription', finalMeasure);
    }
    
    // Set element type if available
    if (selectedDefect.elementType || selectedDefect.element_type || selectedDefect.element) {
      setFieldValue('elementType', selectedDefect.elementType || selectedDefect.element_type || selectedDefect.element);
    }

    // if (selectedDefect.elementType || selectedDefect.element_type || selectedDefect.element) {
    //   setFieldValue('elementType', selectedDefect.elementType || selectedDefect.element_type || selectedDefect.element);
    // }
    
    if (selectedDefect.priority) {
      setFieldValue('priority', selectedDefect.priority);
    }
    
    if (selectedDefect.risk_type || selectedDefect.riskType || selectedDefect.risk_level) {
      setFieldValue('riskType', 
        selectedDefect.risk_type || 
        selectedDefect.riskType || 
        selectedDefect.risk_level
      );
    }
    
    if (selectedDefect.recommendations || selectedDefect.advice) {
      setFieldValue('recommendations', selectedDefect.recommendations || selectedDefect.advice);
    }

    setShowDefectSelection(false);
    setDetectedDefects([]);
    enqueueSnackbar('Defect selected and form updated!', { variant: 'success' });
    
    // Scroll to the element type field at the top to show generated content
    setTimeout(() => {
      // Find the dialog content container and scroll it to top with smooth animation
      const dialogContent = document.querySelector('.MuiDialogContent-root');
      const formElement = document.querySelector('form');
      
      if (dialogContent) {
        // Smooth scroll the dialog content to top
        dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (formElement) {
        // Smooth scroll the form itself
        formElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Also scroll window to top with smooth animation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  };

  // Cancel defect selection
  const handleCancelSelection = () => {
    setShowDefectSelection(false);
    setDetectedDefects([]);
  };

  //   rowDefectIdent: stableRow?.defectIdent,
  //   rowType: stableRow?.type,
  //   hasValues: !!values,
  //   timestamp: new Date().toISOString()
  // });

  return (
    <>
      <Card sx={{ 
        mt: 2, 
        mb: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'visible'
      }}>
        <CardContent sx={{ 
          p: 3
        }}>
          {/* Header with improved styling and compact warning */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 2,
            borderBottom: '1px solid #f0f0f0'
          }}>
            <Camera sx={{ 
              mr: 2, 
              color: 'primary.main',
              fontSize: '1.5rem'
            }} />
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {getIntlTranslation('defect-images')}
              
              {/* Compact Warning Info Badge - positioned right next to title */}
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#fff' }}>
                      💡 Image Upload Guide
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                      <strong>Using AI?</strong> Upload a photo and click Generate with AI — fields auto-fill and saving will include the image.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                      <strong>Not using AI?</strong> Save the image on the image card first, then save the defect.
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', opacity: 0.9 }}>
                      Both workflows ensure your images are properly saved with the defect.
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    bgcolor: '#1976d2',
                    maxWidth: 400,
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#1976d2',
                    p: 0.5,
                    '&:hover': { 
                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            
            <Box sx={{ flex: 1 }} />
            
            {isGenerating && (
              <Chip 
                icon={<CircularProgress size={16} />}
                label="Analyzing..."
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Defect Image Uploader */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <DefectImageUploader
              key={`uploader-${stableRow?.defectIdent || 'new'}`}
              row={stableRow}
              onClose={handleClose}
              needGridRefresh={needGridRefresh}
              hideTopBar={true}  // Hide the save image bar for this context
              onFilesChange={handleUploaderFilesChange}  // 🆕 Receive files from child
              externalCaptionUpdate={externalCaptionUpdate}  // 🆕 Pass caption update trigger
              {...otherProps}
            />
          </Box>

          {/* Enhanced Generate Defect Button */}
          {showGenerateButton && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: 'primary.main',
                  mb: 1,
                  fontSize: '1.1rem'
                }}>
                  {getIntlTranslation('ai-powered')}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ maxWidth: 350, mx: 'auto', fontSize: '0.875rem' }}
                >
                  {isButtonEnabled 
                    ? getIntlTranslation('ai-text-2')
                    : getIntlTranslation('ai-text')
                  }
                </Typography>
              </Box>              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateDefect}
                disabled={isGenerating || !isButtonEnabled}
                startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <Generate />}
                fullWidth
                sx={{
                  py: 1.2,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: isButtonEnabled 
                    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    : '#e0e0e0',
                  '&:hover': isButtonEnabled ? {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                  } : {},
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {isGenerating ? getIntlTranslation('analyzing-image'): 
                 !isButtonEnabled ? getIntlTranslation('upload-image-first'): 
                 getIntlTranslation('generate-defect-with-ai')}
              </Button>
            </Paper>
          )}

          {/* Error Display */}
          {generateError && (
            <Paper 
              elevation={0}
              sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: '#ffebee', 
                border: '1px solid #f44336',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <WarningIcon sx={{ color: '#f44336' }} />
              <Typography variant="body2" sx={{ color: '#c62828', flex: 1 }}>
                {generateError}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setGenerateError(null)}
                sx={{ color: '#f44336' }}
              >
                <Close />
              </IconButton>
            </Paper>
          )}

          {/* Help Text */}
          {!isGenerating && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  fontStyle: 'italic',
                  bgcolor: 'rgba(25, 118, 210, 0.05)',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid rgba(25, 118, 210, 0.1)'
                }}
              >
                {getIntlTranslation('ai-tip')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Multiple Defects Selection Dialog */}
      <Dialog 
        open={showDefectSelection} 
        onClose={handleCancelSelection}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckIcon sx={{ mr: 2, color: 'success.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Multiple Defects Detected
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Our AI found {detectedDefects.length} possible defects. Please select the most appropriate one:
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 0 }}>
            {detectedDefects.map((defect, index) => (
              <Fragment key={index}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleDefectSelect(defect)}
                    sx={{ 
                      py: 3,
                      px: 3,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.05)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {defect.description || defect.defect || defect.title || `Defect ${index + 1}`}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {(defect.elementType || defect.element_type || defect.element) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Element:</strong> {defect.elementType || defect.element_type || defect.element}
                            </Typography>
                          )}
                          {(defect.recommended_action || defect.measure || defect.action) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Action:</strong> {defect.recommended_action || defect.measure || defect.action}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {(defect.elementType || defect.element_type || defect.element) && (
                              <Chip 
                                label={defect.elementType || defect.element_type || defect.element} 
                                size="small" 
                                color="info" 
                                variant="outlined" 
                              />
                            )}
                            {defect.priority && (
                              <Chip 
                                label={`Priority: ${defect.priority}`} 
                                size="small" 
                                color="primary" 
                                variant="outlined" 
                              />
                            )}
                            {(defect.risk_type || defect.riskType || defect.risk_level) && (
                              <Chip 
                                label={`Risk: ${defect.risk_type || defect.riskType || defect.risk_level}`} 
                                size="small" 
                                color="warning" 
                                variant="outlined" 
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < detectedDefects.length - 1 && <Divider />}
              </Fragment>
            ))}
          </List>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCancelSelection}
            startIcon={<CancelIcon />}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

DefectImageUploaderWithGenerate.propTypes = {
  row: PropTypes.shape({
    defectIdent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trade: PropTypes.object,
    type: PropTypes.string,
    deleted: PropTypes.bool,
    imageCount: PropTypes.number
  }),
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  showGenerateButton: PropTypes.bool,
  onClose: PropTypes.func,
  needGridRefresh: PropTypes.func,
  onSaveImages: PropTypes.func
};

DefectImageUploaderWithGenerate.defaultProps = {
  showGenerateButton: true,
  row: null
};

// Memoize component with proper comparison to prevent unnecessary re-renders
export default memo(DefectImageUploaderWithGenerate, (prevProps, nextProps) => {
  // Compare all relevant props to prevent re-renders
  const rowChanged = prevProps.row?.defectIdent !== nextProps.row?.defectIdent ||
                    prevProps.row?.imageCount !== nextProps.row?.imageCount ||
                    prevProps.row?.trade?.value !== nextProps.row?.trade?.value;
  
  const valuesChanged = prevProps.values?.defectIdent !== nextProps.values?.defectIdent ||
                       JSON.stringify(prevProps.values) !== JSON.stringify(nextProps.values);
  
  const propsChanged = prevProps.showGenerateButton !== nextProps.showGenerateButton;
  
  // Return true if props are the same (don't re-render), false if different (re-render)
  return !rowChanged && !valuesChanged && !propsChanged;
});

export { DefectImageUploaderWithGenerate };
